//
//  people.ts
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: October 26, 2026
//  Author: Bob Keathley
//
//  Types, paths and helpers for a person's profile (SOUL.md), personas and AI
//  accounts (spec §6w, SIG-176): the wire shapes the Person and AI accounts
//  pages read, who may decide a corporate account, and the refusal wording.
//
import { atLeast } from '../auth'

export type PersonaKind = 'work' | 'family' | 'community' | 'personal'
/** For `family` and `personal` the server keeps only id, label, kind and source; the rest is absent. */
export type Persona = {
  id: string
  label: string
  kind: PersonaKind
  source: 'claude-suggested' | 'person'
  priorities?: string
  tone?: string
  limits?: string | string[]
  teams?: string[]
  projects?: string[]
}
export type Profile = {
  version: number
  soul_md: string
  sha256: string
  generated_by: string
  edited_by_person: boolean
  machine_id: string | null
  submitted_at: string
  personas: Persona[]
}
/** `GET /people/me/profile` (with `versions`) and `GET /levels/users/{id}/profile` (with `signed_profile_version`). */
export type ProfileBody = {
  current: Profile | null
  versions?: { version: number; sha256: string; generated_by: string; submitted_at: string; persona_count: number }[]
  signed_profile_version?: number | null
}

export type AiProvider = 'anthropic' | 'openai' | 'google' | 'other'
export type AiStatus = 'personal' | 'proposed_corporate' | 'approved_corporate' | 'rejected'
export type AiAccount = {
  id: string
  user_id: string | null
  provider: AiProvider
  account_label: string
  account_identity: Record<string, string>
  kind_claimed: 'personal' | 'corporate'
  status: AiStatus
  decided_by: string | null
  decided_at: string | null
  reason: string | null
  created_at: string
  owner?: { user_id: string; email: string; name: string } | null
}

/** Accepts the flat profile or the `{current, …}` body, and returns the body shape the pages read. */
export function normalizeProfile(p: Profile | ProfileBody | null | undefined, signedVersion: number | null = null): ProfileBody | null {
  if (!p) return null
  if ('current' in p) return p
  return { current: p, signed_profile_version: signedVersion }
}

export const removeCorporatePath = (id: string) => `/ai-accounts/${id}`
export const profilePath = (userId: string) => `/levels/users/${userId}/profile`
export const sendBackPath = (userId: string) => `/levels/users/${userId}/notebook/send-back`
export const aiAccountsPath = (status: AiStatus | 'all', limit = 100) => `/ai-accounts?status=${status}&limit=${limit}`
export const myAiAccountsPath = '/people/me/ai-accounts'
export const decideAccountPath = (id: string, verb: 'approve' | 'reject') => `/ai-accounts/${id}/${verb}`

/** Only an owner or security-admin decides corporate accounts, and sees the queue and the allowlist (§6w). */
export const canDecideAccounts = (role: string | undefined) => atLeast(role, 'security-admin')

export const providerLabel = (p: string) => (p === 'anthropic' ? 'Anthropic' : p === 'openai' ? 'OpenAI' : p === 'google' ? 'Google' : 'Other')
export const kindLabel = (k: PersonaKind) => (k === 'work' ? 'Work' : k === 'family' ? 'Family' : k === 'community' ? 'Community' : 'Personal')
/** Family and personal personas show only label and kind; their details stay on the Mac. */
export const isPrivateKind = (k: PersonaKind) => k === 'family' || k === 'personal'
export const identityText = (id: Record<string, string> | null | undefined) => Object.values(id ?? {}).filter(Boolean).join(' · ')

/** Human text for the §6w refusals; falls back to the server's message. */
export function peopleErrorText(e: { code: string; message: string; details?: unknown }): string {
  const d = (e.details ?? {}) as { code?: string; reason?: string }
  const sub = d.code ?? d.reason
  if (e.code === 'own_account') return 'Another admin decides your own accounts.'
  if (e.code === 'assumed_read_only') return 'This assumed session is read-only.'
  if (e.code === 'forbidden') return 'Your role may not do this.'
  if (e.code === 'not_found') return 'That account no longer exists.'
  if (sub === 'not_approved') return 'Only an approved corporate account can be removed here.'
  if (sub === 'already_decided') return 'That account was already decided.'
  if (sub === 'account_exists') return 'That account is already on the list.'
  if (sub === 'reason_required') return 'A reason is required.'
  if (sub === 'secret_in_identity') return 'The identity looks like a credential. Enter only an email or organization, never a token or key.'
  if (sub === 'bad_provider') return 'That provider is not recognised.'
  return e.message
}
